<script setup>
    import { computed } from 'vue';
    import HeatmapChart from '@/components/result/ai-literacy-2026/HeatmapChart.vue';
    import { AREA_CODES, AREA_NAMES, CLASS_TEACHING_RESOURCES } from '@/components/result/ai-literacy-2026/data/reportData';
    import { questionsByArea, classStrengthComment } from '@/components/result/ai-literacy-2026/data/reportLogic';
    import icon01 from '@/assets/images/result/ai/icon_01.svg';
    import icon02 from '@/assets/images/result/ai/icon_02.svg';
    import icon03 from '@/assets/images/result/ai/icon_03.svg';
    import icon04 from '@/assets/images/result/ai/icon_04.svg';
    import qrImage from '@/assets/images/result/ai_qr.png';
    import qrFrame from '@/assets/images/result/ai/qr_frame.svg';

    const props = defineProps({
        teacherData: Object
    });

    const icons = [icon01, icon02, icon03, icon04];
    const fieldInfo = AREA_CODES.reduce((acc, code, i) => {
        acc[code] = { name: AREA_NAMES[i], icon: icons[i] };
        return acc;
    }, {});

    // 영역별 정답률 히트맵
    const sampleData = computed(() =>
        AREA_NAMES.map((category, i) => ({
            category,
            rate: parseFloat(props.teacherData?.[`field${i + 1}_rate`]) || 0
        }))
    );

    const schulType = computed(() => props.teacherData?.schul_type || 'E');

    const resolveField = (raw, fallback) => (fieldInfo[raw] ? raw : fallback);

    // 강점 영역 (strong_field, 학급별영역별강약점 시트 's')
    const strengthInfo = computed(() => {
        const field = resolveField(props.teacherData?.strong_field, 'd');
        return { field, ...fieldInfo[field], description: classStrengthComment(schulType.value, field, 's') };
    });
    // 약점 영역 (weak_field, 시트 'w')
    const weaknessInfo = computed(() => {
        const field = resolveField(props.teacherData?.weak_field, 'b');
        return { field, ...fieldInfo[field], description: classStrengthComment(schulType.value, field, 'w') };
    });

    // strong1~3 / weak1~3_materials(문항번호) → 문항분류 시트의 주제 + 학급 정답률(q_N_correct_rate)
    const buildQuestions = (prefix) => {
        const byArea = questionsByArea(schulType.value);
        return [1, 2, 3]
            .map(i => props.teacherData?.[`${prefix}${i}_materials`])
            .filter(qNo => qNo)
            .map(qNo => {
                let subject = '문항';
                let questionNumber = qNo; // 영역 내 순번으로 표시 (2025 방식 유지)
                for (const code of AREA_CODES) {
                    const idx = byArea[code].findIndex(q => Number(q.q_no) === Number(qNo));
                    if (idx !== -1) { subject = byArea[code][idx].subject; questionNumber = idx + 1; break; }
                }
                const rate = parseFloat(props.teacherData?.[`q_${qNo}_correct_rate`]) || 0;
                return { qNo: questionNumber, subject, rate: rate.toFixed(1) };
            });
    };
    const strengthQuestions = computed(() => buildQuestions('strong'));
    const weaknessQuestions = computed(() => buildQuestions('weak'));

    // 수업 도움자료 (엑셀 '학급용 수업도움자료' 시트) — 영역별 묶음
    const teachingResources = computed(() =>
        AREA_NAMES.map(name => ({
            area: name,
            items: (CLASS_TEACHING_RESOURCES.items || []).filter(r => r.area === name)
        })).filter(g => g.items.length)
    );
    const ecosystem = CLASS_TEACHING_RESOURCES.ecosystem || { title: '', url: '' };
    // 엑셀에 줄바꿈 시안이 적혀 있으면 그대로 끊고, 없으면 한 줄로 두고 자연 줄바꿈에 맡긴다
    const ecosystemTitleLines = ecosystem.titleLines?.length ? ecosystem.titleLines : [ecosystem.title];

    // 자료명에 <인간과 인공지능> 같은 부제가 붙으면 그 앞에서 줄을 바꾼다
    const titleLines = (title) => {
        const text = String(title || '');
        const i = text.indexOf('<');
        return i > 0 ? [text.slice(0, i).trim(), text.slice(i)] : [text];
    };
</script>

<template>
  <div class="report-inner">   
    <div class="card-wrap">
        <HeatmapChart :data="sampleData" />        
        <div class="card mt-4">
            <div class="ai-table primary">
                <div class="row thead">
                    <div class="th col">
                        <div class="th">구분</div>
                        <div class="th">영역</div>
                    </div>                    
                    <div class="td">설명</div>
                </div>
                <div class="row">
                    <div class="th col">
                        <div class="th">강점</div>
                        <div class="th">
                             <img :src="strengthInfo.icon">
                            <p>{{ strengthInfo.name }}</p>
                        </div>
                    </div>
                    <div class="td">
                        {{ strengthInfo.description }}
                        <ul>
                            <li v-for="question in strengthQuestions" :key="question.qNo + question.subject">
                                {{ question.subject }}: {{ question.rate }}%
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="row">
                    <div class="th col">
                        <div class="th">약점</div>
                        <div class="th">
                             <img :src="weaknessInfo.icon">
                            <p>{{ weaknessInfo.name }}</p>
                        </div>
                    </div>
                    <div class="td">
                        {{ weaknessInfo.description }}
                        <ul>
                            <li v-for="question in weaknessQuestions" :key="question.qNo + question.subject">
                                {{ question.subject }}: {{ question.rate }}%
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="row help-row">
                    <div class="th col">
                        <div class="th">AI·디지털<br>리터러시<br>수업 도움자료<br>(공통자료)</div>
                        <!-- 영역명은 위 행의 '영역' 칸과 같은 자리에 오도록 첫 칸 안에 둔다 -->
                        <div class="th help-area-names">
                            <p v-for="group in teachingResources" :key="group.area">{{ group.area }}</p>
                        </div>
                    </div>
                    <div class="td help-td">
                        <!-- 영역마다 자료 2개씩, 각 자료에 '바로가기' 버튼 -->
                        <div class="help-areas">
                            <ul class="area-items" v-for="group in teachingResources" :key="group.area">
                                <li v-for="item in group.items" :key="item.url">
                                    <span class="item-title">
                                        <span v-for="(line, li) in titleLines(item.title)" :key="li" class="title-line">{{ line }}</span>
                                    </span>
                                    <a :href="item.url" target="_blank" class="go-link">바로가기 <span class="arrow">&gt;</span></a>
                                </li>
                            </ul>
                        </div>
                        <div class="qr-area">
                            <p class="qr-title">
                                <span v-for="(line, li) in ecosystemTitleLines" :key="li" class="title-line">{{ line }}</span>
                            </p>
                            <div class="qr-placeholder" :style="{ backgroundImage: `url(${qrFrame})` }">
                                <div class="qr-img"><img :src="qrImage" alt="QR코드"></div>
                            </div>
                            <a :href="ecosystem.url" target="_blank" class="go-link">바로가기 <span class="arrow">&gt;</span></a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ai-table {
    // 표 칸은 overflow:hidden이라 그리드가 내용보다 작게 줄여 잘라낸다.
    // 이 행은 내용 높이를 그대로 따라야 왼쪽 영역명과 오른쪽 자료 줄이 맞는다.
    .row.help-row > div { overflow: visible; }
    // 수업 도움자료 행: 영역명 칸을 4등분해 오른쪽 자료 묶음과 줄을 맞춘다
    .row.help-row .th.help-area-names {
        padding: 0 !important;
        flex-direction: column;
        align-items: stretch !important;
        p {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: .5rem;
            text-align: center;
            font-weight: 600;
            color: var(--result-ai-primary-800);
            word-break: keep-all;
            & + p { border-top: 1px solid var(--result-ai-primary-100); }
        }
    }
    .row div.th.col {
        padding: 0;
       display: flex;  
        &::before { display: none; }
        .th {
            position: relative;
            padding: .75rem 0 .75rem 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 200px;
            height: 100%;
            min-width: auto;
            &::before {
                content: "";
                position: absolute;
                background-color: var(--result-ai-primary-100);
                top: 50%;
                right: 0;
                transform: translateY(-50%);
                width: 1px;
                height: calc(100% - 10px);
            }
            &:nth-child(1) { font-weight: 600; text-align: center; width: 150px; }
            &:nth-child(2) { 
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 180px;
                p { font-weight: 600; color: var(--result-ai-primary-800); }
            }
        }  
    }
    .row:not(.thead):not(.help-row) > .td {
        // 강점/약점 설명 칸 — 전역 기본값(1.25rem)이면 4p가 넘친다.
        // 전역 규칙(.ai-table .row > div)과 명시도가 같아서 이겨야 하니 !important로 못박는다.
        // (help-row의 .help-td는 자체 padding:0!important가 있어 여기서 제외해야 한다)
        padding: .75rem 1.25rem !important;
    }
    .td {
        line-height: 1.5;
        text-align: justify;
        word-break: keep-all;
        text-justify: inter-word;
         > ul {
            li { 
            position: relative;
            padding-left: 24px;
            &::before {
                content: '';
                position: absolute;
                left: 10px;
                top: 11px;
                width: 3px;
                height: 3px;
                background-color: var(--result-ai-blak);
                border-radius: 50%;       
                }
            }
        }
        // 수업 도움자료: 영역 | 자료명+바로가기 | QR 세 칸 구성
        &.help-td {
            display: flex; // 자료 목록 | QR 가로 배치
            padding: 0 !important;
            min-width: 0; // 칸이 줄어들 수 있어야 표 밖으로 넘치지 않는다
        }
        .help-areas {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            .area-items {
                flex: 1;
                min-width: 0;
                min-height: 100px; // 자료명 두 줄 + 상하 여백
                display: flex;
                flex-direction: column;
                justify-content: center;
                & + .area-items { border-top: 1px solid var(--result-ai-primary-100); }
                li {
                    // 영역 안에서 항목이 정확히 절반씩 차지해야
                    // 왼쪽 영역명 칸의 구분선과 어긋나지 않는다
                    flex: 1;
                    min-height: 0;
                    display: flex;
                    align-items: center;
                    gap: .75rem;
                    padding: .8rem .875rem;
                    &::before { display: none; }
                    & + li { border-top: 1px dashed var(--result-ai-primary-100); }
                    .item-title {
                        flex: 1;
                        min-width: 0;
                        .title-line { display: block; }
                        // 자료명이 모두 두 줄에 들어가야 영역 구분선이 왼쪽 칸과 어긋나지 않는다
                        font-size: .9375rem;
                        line-height: 1.4;
                        word-break: keep-all;
                        text-align: left;
                    }
                }
            }
        }
        .go-link {
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 3px 12px;
            border: 1px solid var(--result-ai-primary-500);
            border-radius: 6px;
            background: #fff;
            color: var(--result-ai-primary-500);
            font-size: 13px;
            font-weight: 600;
            line-height: 1.6;
            text-decoration: none;
            white-space: nowrap;
            width: auto;
            .arrow { font-size: 11px; }
        }
        .qr-area {
            flex: 0 0 200px;
            padding: .75rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            position: relative;
            p { text-align: center; font-weight: 600; color: var(--result-ai-primary-800); }
            .qr-title {
                font-size: 1.0625rem;
                line-height: 1.4;
                word-break: keep-all;
                .title-line { display: block; }
            }
            // URL 대신 버튼만 노출 (a 기본 규칙이 밑줄·전체폭이라 여기서 되돌린다)
            a.go-link {
                width: auto;
                margin-top: .75rem;
                text-decoration: none;
                text-align: center;
                word-break: keep-all;
                font-size: 13px;
                line-height: 1.6;
            }
            .qr-placeholder {
                position: relative;
                width: 162px;
                height: 232px;
                .qr-img {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                    img {
                        margin-top: 40px;
                        width: 109px;
                        height: 111px;
                    }
                }
            }
            &::before {
                content: "";
                position: absolute;
                background-color: var(--result-ai-primary-100);
                top: 50%;
                left: 0;
                transform: translateY(-50%);
                width: 1px;
                height: calc(100% - 10px);
            }
        }
    }
    .row.thead div.th.col .th {
        &::before {
            width: 2px !important;
            height: 100% !important;
        }
    }    
}
</style>